import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaEye, FaFileAlt, FaCheck, FaTimes, FaComment } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import {
    acceptCampaignOffer,
    confirmCampaignContract,
    getCampaignNegotiationThread,
    sendCampaignCounterOffer,
    signCampaignContract,
} from '../../services/affiliateService';
import { CampaignBriefModal } from './CampaignBriefModal';

function brandInitials(name) {
    if (!name || !String(name).trim()) return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    const s = parts[0];
    return s.length >= 2 ? s.slice(0, 2).toUpperCase() : `${s[0]}`.toUpperCase();
}

function getRateParts(campaign) {
    const c = campaign.agreement?.commission || campaign.commission;
    if (!c) return { main: '—', showIndicative: false };
    if (c.model === 'fixed') {
        const sym = c.currency?.symbol || '₹';
        const amt = c.amount != null ? Number(c.amount).toLocaleString('en-IN') : '';
        return { main: `${sym}${amt}`, showIndicative: true };
    }
    return { main: `${c.percentage ?? '—'}%`, showIndicative: true };
}

function campaignTypeClass(campaignType) {
    if (campaignType === 'Hybrid') return 'text-orange-600';
    if (campaignType === 'Advertising') return 'text-blue-600';
    if (campaignType === 'Affiliate') return 'text-blue-600';
    return 'text-gray-900';
}

function getBrandOfferParts(campaign) {
    const a = campaign.agreement?.commission;
    if (!a) return { main: '—', hasOffer: false };
    if (a.model === 'fixed' && a.amount != null && a.amount !== '') {
        const sym = a.currency?.symbol || '₹';
        return { main: `${sym}${Number(a.amount).toLocaleString('en-IN')}`, hasOffer: true };
    }
    if (a.model === 'percentage' && a.percentage != null) {
        return { main: `${a.percentage}%`, hasOffer: true };
    }
    return { main: '—', hasOffer: false };
}

const ACCEPTED_STEP_LABELS = ['Invited', 'Accepted', 'Negotiating', 'Contracting', 'Confirmed'];

/** 0–4: which step is active (orange). Prior steps are complete (green). */
function getAcceptedCampaignCurrentStepIndex(campaign, optimisticPricingAgreed) {
    const pn = campaign.pipelineNegotiation;
    const stageId = pn?.stageId;
    const pricingDone =
        optimisticPricingAgreed ||
        campaign.agreement?.status === 'agreed_with_influencer' ||
        pn?.negotiationStatus === 'agreed';
    if (stageId === 'confirmed') return 4;
    if (stageId === 'contracted') return 3;
    if (pricingDone) return 3;
    return 2;
}

// function AcceptedCampaignProgressStepper({ currentStepIndex }) {
//     const linePct = Math.min(100, (currentStepIndex / 4) * 100);
//     const lineBg = `linear-gradient(90deg, #22c55e 0%, #22c55e ${linePct}%, #e5e7eb ${linePct}%, #e5e7eb 100%)`;
//     return (
//         <div className="px-4 sm:px-5 pb-4 pt-1">
//             <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-4">
//                 Campaign progress
//             </p>
//             <div className="relative px-1 sm:px-3">
//                 <div
//                     className="absolute left-[9%] right-[9%] top-[15px] h-0.5 z-0 hidden sm:block rounded-full"
//                     style={{ background: lineBg }}
//                     aria-hidden
//                 />
//                 <div className="relative flex justify-between items-start gap-0 z-10">
//                     {ACCEPTED_STEP_LABELS.map((label, i) => {
//                         const allDone = currentStepIndex >= 4;
//                         const isComplete = allDone || i < currentStepIndex;
//                         const isCurrent = !allDone && i === currentStepIndex;

//                         let circleClass =
//                             'w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ';
//                         let labelClass =
//                             'mt-2 text-[10px] sm:text-xs text-center max-w-[4.2rem] sm:max-w-none leading-tight px-0.5';

//                         if (isComplete && !isCurrent) {
//                             circleClass += 'bg-green-500 border-green-500 text-white';
//                             labelClass += ' font-medium text-green-600';
//                         } else if (isCurrent) {
//                             circleClass +=
//                                 'bg-orange-500 border-orange-400 text-white shadow-[0_0_0_4px_rgba(251,146,60,0.25)]';
//                             labelClass += ' font-bold text-orange-600';
//                         } else {
//                             circleClass += 'bg-white border-gray-300 text-gray-400';
//                             labelClass += ' font-medium text-gray-500';
//                         }

//                         if (currentStepIndex === 4 && i === 4) {
//                             labelClass += ' font-bold';
//                         }

//                         return (
//                             <div key={label} className="flex flex-col items-center flex-1 min-w-0">
//                                 <div className={circleClass}>
//                                     {isComplete && !isCurrent ? (
//                                         <FaCheck className="text-[10px] sm:text-xs" aria-hidden />
//                                     ) : isCurrent ? (
//                                         <span className="w-1.5 h-1.5 rounded-full bg-white block" aria-hidden />
//                                     ) : (
//                                         <span>{i + 1}</span>
//                                     )}
//                                 </div>
//                                 <span className={labelClass}>{label}</span>
//                             </div>
//                         );
//                     })}
//                 </div>
//             </div>
//             <div
//                 className="sm:hidden mt-3 h-0.5 rounded-full mx-3"
//                 style={{ background: lineBg }}
//                 aria-hidden
//             />
//         </div>
//     );
// }

function AcceptedCampaignProgressStepper({ currentStepIndex }) {
    const linePct = Math.min(100, (currentStepIndex / 4) * 100);
    const lineBg = `linear-gradient(90deg, #22c55e 0%, #22c55e ${linePct}%, #e5e7eb ${linePct}%, #e5e7eb 100%)`;

    return (
        <div className="px-4 sm:px-5 pb-4 pt-1">
            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-4">
                Campaign progress
            </p>

            {/* 🔥 IMPORTANT: relative parent */}
            <div className="relative px-1 sm:px-3">

                {/* ✅ SINGLE unified progress line (mobile + desktop) */}
                <div
                    className="absolute left-[10%] right-[10%] top-[11px] sm:top-[11px] h-0.5 z-0 rounded-full"
                    style={{ background: lineBg }}
                    aria-hidden
                />

                {/* Steps */}
                <div className="relative flex justify-between items-start gap-0 z-10">
                    {ACCEPTED_STEP_LABELS.map((label, i) => {
                        const allDone = currentStepIndex >= 4;
                        const isComplete = allDone || i < currentStepIndex;
                        const isCurrent = !allDone && i === currentStepIndex;

                        let circleClass =
                            'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ';
                        let labelClass =
                            'mt-2 text-[10px] sm:text-xs text-center max-w-[4.2rem] sm:max-w-none leading-tight px-0.5';

                        if (isComplete && !isCurrent) {
                            circleClass += 'bg-green-500 border-green-500 text-white';
                            labelClass += 'font-medium text-green-600';
                        } else if (isCurrent) {
                            circleClass +=
                                'bg-orange-500 border-orange-400 text-white shadow-[0_0_0_4px_rgba(251,146,60,0.25)]';
                            labelClass += 'font-bold text-orange-600';
                        } else {
                            circleClass += 'bg-white border-gray-300 text-gray-400';
                            labelClass += 'font-medium text-gray-500';
                        }

                        if (currentStepIndex === 4 && i === 4) {
                            labelClass += ' font-bold';
                        }

                        return (
                            <div key={label} className="flex flex-col items-center flex-1 min-w-0">
                                <div className={circleClass}>
                                    {isComplete && !isCurrent ? (
                                        <FaCheck className="text-[10px] sm:text-xs" aria-hidden />
                                    ) : isCurrent ? (
                                        <span className="w-1.5 h-1.5 rounded-full bg-white block" aria-hidden />
                                    ) : (
                                        <span>{i + 1}</span>
                                    )}
                                </div>

                                <span className={labelClass}>{label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export const CampaignCard = ({
    campaign,
    companyName,
    brandName,
    companyEmail,
    companyPhone,
    setShowAnalytics,
    setCommissionModal,
    setBrandDetailsModal,
    selectedCampaign,
    setSelectedCampaign,
    isDialogOpen,
    setIsDialogOpen,
    selectedMediaType,
    setSelectedMediaType,
    youtubeUrls,
    setYoutubeUrls,
    newYoutubeLink,
    setNewYoutubeLink,
    instagramUrls,
    setInstagramUrls,
    newInstagramLink,
    setNewInstagramLink,
    mediaError,
    mediaSuccess,
    mediaLoading,
    handleAddMedia,
    openConfirmationModal,
    toast,
    onCampaignsRefresh,
}) => {
    const rateParts = getRateParts(campaign);
    const startStr =
        campaign.startDate && !isNaN(new Date(campaign.startDate).getTime())
            ? new Date(campaign.startDate).toLocaleDateString()
            : 'Not set';
    const expiresStr =
        campaign.expiresAt && !isNaN(new Date(campaign.expiresAt).getTime())
            ? new Date(campaign.expiresAt).toLocaleDateString()
            : 'Not set';

    // Negotiation thread state (must not be conditional hooks)
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [counterAmount, setCounterAmount] = useState('');
    const [counterMessage, setCounterMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [accepting, setAccepting] = useState(false);
    const [briefModalOpen, setBriefModalOpen] = useState(false);
    const [optimisticPricingAgreed, setOptimisticPricingAgreed] = useState(false);
    const [influencerSignLoading, setInfluencerSignLoading] = useState(false);
    const [confirmCampaignLoading, setConfirmCampaignLoading] = useState(false);
    const [fullContractOpen, setFullContractOpen] = useState(false);

    const token = campaign?.token;
    const campaignId = campaign?._id;

    const brandOfferAmountInr = useMemo(() => {
        const a = campaign?.agreement?.commission;
        if (!a) return null;
        if (a.model === 'fixed') return Number(a.amount ?? 0);
        return null;
    }, [campaign?.agreement?.commission]);

    const latestBrandOfferInr = useMemo(() => {
        if (!Array.isArray(thread) || thread.length === 0) return null;
        for (let i = thread.length - 1; i >= 0; i -= 1) {
            const m = thread[i];
            if (m?.actor === 'company' && m?.amountInr != null) {
                const n = Number(m.amountInr);
                if (Number.isFinite(n) && n >= 0) return n;
            }
        }
        return null;
    }, [thread]);

    useEffect(() => {
        setOptimisticPricingAgreed(false);
    }, [campaign._id]);

    useEffect(() => {
        if (campaign?.status !== 'accepted') return;
        if (!token || !campaignId) return;
        let cancelled = false;
        setThreadLoading(true);
        getCampaignNegotiationThread(token, campaignId)
            .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res?.data?.thread) ? res.data.thread : [];
                setThread(list);
            })
            .catch(() => {
                if (cancelled) return;
                setThread([]);
            })
            .finally(() => {
                if (cancelled) return;
                setThreadLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [campaign?.status, token, campaignId]);

    const handleInfluencerContractSign = useCallback(async () => {
        if (!token || !campaignId) return;
        setInfluencerSignLoading(true);
        try {
            const signedBy = String(campaign?.influencerName || '').trim() || 'Influencer';
            await signCampaignContract(token, campaignId, { signedBy });
            toast.success('Signature saved');
            onCampaignsRefresh?.();
        } catch (e) {
            toast.error(e?.message || 'Failed to sign contract');
        } finally {
            setInfluencerSignLoading(false);
        }
    }, [token, campaignId, campaign?.influencerName, toast, onCampaignsRefresh]);

    const handleConfirmCampaign = useCallback(async () => {
        if (!token || !campaignId) return;
        setConfirmCampaignLoading(true);
        try {
            await confirmCampaignContract(token, campaignId);
            toast.success('Campaign confirmed');
            onCampaignsRefresh?.();
            setFullContractOpen(false);
        } catch (e) {
            toast.error(e?.message || 'Failed to confirm campaign');
        } finally {
            setConfirmCampaignLoading(false);
        }
    }, [token, campaignId, toast, onCampaignsRefresh]);

    if (campaign.status === 'pending') {
        return (
            <>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                className="bg-white rounded-[10px] border border-gray-200/90 shadow-sm overflow-hidden mb-4"
            >
                {/* Top: avatar + title + pending badge */}
                <div className="p-4 sm:p-5 flex gap-4 items-start">
                    <div
                        className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 flex items-center justify-center shrink-0 text-orange-700 font-bold text-sm sm:text-base"
                        aria-hidden
                    >
                        {brandInitials(brandName || companyName)}
                    </div>
                    <div className="flex-1 min-w-0 pr-2">
                        <p className="text-xs sm:text-sm text-gray-500 truncate">
                            {companyName} / {brandName}
                        </p>
                        <h3 className="text-sm sm:text-lg font-bold text-gray-900 mt-0.5 leading-snug truncate">
                            {campaign.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                            {campaign.description || 'No description provided'}
                        </p>
                    </div>
                    <div className="shrink-0 pt-0.5">
                        <span className="inline-flex items-center text-xs sm:text-sm font-semibold text-orange-500 whitespace-nowrap">
                            <span className="mr-1">•</span> Pending
                        </span>
                    </div>
                </div>

                {/* Data row */}
                <div className="bg-gray-100/90 border-t border-gray-200/80 px-3 sm:px-5 py-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-4 text-center sm:text-left">
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Type
                            </p>
                            <p className={`text-sm font-bold ${campaignTypeClass(campaign.campaignType)}`}>
                                {campaign.campaignType || '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Commission
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    const commissionForDisplay = campaign.agreement?.commission || campaign.commission;
                                    setCommissionModal({
                                        isOpen: true,
                                        commission: commissionForDisplay,
                                        campaignName: campaign.name,
                                        campaignType: campaign.campaignType,
                                        agreementStatus: campaign.agreement?.status || '',
                                    });
                                }}
                                className="inline-flex items-center justify-center sm:justify-start gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm w-full sm:w-auto"
                            >
                                <FaEye className="text-xs shrink-0" />
                                <span>View details</span>
                            </button>
                        </div>
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Brand
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    setBrandDetailsModal({
                                        isOpen: true,
                                        companyName: companyName || '',
                                        brandName: brandName || '',
                                        companyEmail: companyEmail || null,
                                        companyPhone: companyPhone || null,
                                    });
                                }}
                                className="inline-flex items-center justify-center sm:justify-start gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm w-full sm:w-auto"
                            >
                                <FaEye className="text-xs shrink-0" />
                                <span>View details</span>
                            </button>
                        </div>
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Starts
                            </p>
                            <p className="text-sm font-bold text-gray-900">{startStr}</p>
                        </div>
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Expires
                            </p>
                            <p className="text-sm font-bold text-gray-900">{expiresStr}</p>
                        </div>
                        <div>
                            <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                Rate
                            </p>
                            <p className="text-sm font-bold text-gray-900">
                                {rateParts.main}
                                {rateParts.showIndicative && (
                                    <span className="font-normal text-gray-500 text-xs ml-1">indicative</span>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Action footer */}
                <div className="p-4 sm:p-5 pt-4 border-t border-gray-100 bg-white">
                    <p className="text-sm font-bold text-gray-900">Action required — review this invitation</p>
                    <p className="text-sm text-gray-600 mt-1 mb-4">
                        {brandName || companyName} has invited you to {campaign.name}. Review the brief and accept or
                        decline.
                    </p>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-2.5 items-start sm:items-center">
                        <button
                            type="button"
                            onClick={() => setBriefModalOpen(true)}
                            className="inline-flex w-fit items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-800 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
                        >
                            <FaFileAlt className="text-gray-600 text-xs shrink-0" />
                            View full brief
                        </button>
                        <div className="flex flex-row flex-wrap gap-2 w-fit">
                            <button
                                type="button"
                                className="inline-flex w-fit items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[#28a745] hover:bg-[#218838] text-white text-xs sm:text-sm font-semibold transition-colors shadow-sm"
                                onClick={() => openConfirmationModal(campaign.token, campaign._id, 'accept')}
                            >
                                <FaCheck className="text-xs shrink-0" />
                                Accept campaign
                            </button>
                            <button
                                type="button"
                                className="inline-flex w-fit items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[#dc3545] hover:bg-[#c82333] text-white text-xs sm:text-sm font-semibold transition-colors shadow-sm"
                                onClick={() => openConfirmationModal(campaign.token, campaign._id, 'reject')}
                            >
                                <FaTimes className="text-xs shrink-0" />
                                Decline
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
            <CampaignBriefModal
                isOpen={briefModalOpen}
                onClose={() => setBriefModalOpen(false)}
                campaign={campaign}
                companyName={companyName}
                brandName={brandName}
                companyEmail={companyEmail}
                companyPhone={companyPhone}
                onAccept={() => openConfirmationModal(campaign.token, campaign._id, 'accept')}
                onDecline={() => openConfirmationModal(campaign.token, campaign._id, 'reject')}
            />
            </>
        );
    }

    if (campaign.status === 'accepted') {
        const agreed =
            optimisticPricingAgreed ||
            campaign.agreement?.status === 'agreed_with_influencer' ||
            campaign.pipelineNegotiation?.negotiationStatus === 'agreed';
        const pipelineStageId = campaign?.pipelineNegotiation?.stageId || null;
        const contract = campaign?.pipelineNegotiation?.contract || null;
        const paymentTermDaysRaw =
            contract?.paymentTermDays ?? campaign?.pipelineNegotiation?.contract?.paymentTermDays ?? null;
        const hasPaymentTermDays =
            paymentTermDaysRaw !== undefined && paymentTermDaysRaw !== null && paymentTermDaysRaw !== '';
        const paymentTermDaysNum = hasPaymentTermDays ? Number(paymentTermDaysRaw) : null;
        const influencerHasSignedContract = Boolean(contract?.influencerSignedAt);
        const contractFullyExecuted =
            contract?.status === 'fully_signed' ||
            (Boolean(contract?.brandSignedAt) && influencerHasSignedContract);
        const canAddMedia = pipelineStageId === 'confirmed' && contractFullyExecuted;
        const showContractPanel =
            pipelineStageId === 'contracted' && Boolean(contract?.brandSignedAt);
        const canAcceptOffer =
            !agreed && (brandOfferAmountInr != null || latestBrandOfferInr != null);
        const brandOffer = getBrandOfferParts(campaign);
        const brandOfferDisplay =
            brandOffer.hasOffer && brandOffer.main !== '—'
                ? brandOffer.main
                : latestBrandOfferInr != null
                    ? `₹${Number(latestBrandOfferInr).toLocaleString('en-IN')}`
                    : '—';
        const offerForAcceptLabel =
            brandOffer.hasOffer && brandOffer.main !== '—'
                ? brandOffer.main
                : latestBrandOfferInr != null
                    ? `₹${Number(latestBrandOfferInr).toLocaleString('en-IN')}`
                    : rateParts.main;

        const addMediaModal = isDialogOpen && selectedCampaign && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="add-media-dialog-title"
                    className="bg-white text-gray-900 rounded-2xl p-5 sm:p-6 w-full max-w-lg space-y-4 border border-gray-200 shadow-2xl max-h-[90vh] overflow-y-auto"
                >
                    <div className="flex justify-between items-start gap-3 border-b border-gray-100 pb-4">
                        <div className="min-w-0">
                            <p className="text-xs text-gray-500 truncate">
                                {companyName} / {brandName}
                            </p>
                            <h2 id="add-media-dialog-title" className="text-lg font-bold text-gray-900 truncate pr-2">
                                Add media for campaign
                            </h2>
                            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{selectedCampaign?.name}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setIsDialogOpen(false);
                                setSelectedCampaign(null);
                            }}
                            className="p-2 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 shrink-0"
                            aria-label="Close"
                        >
                            <FaTimes className="text-lg" />
                        </button>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Select media type</label>
                        <div className="flex gap-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="mediaType"
                                    value="youtube"
                                    checked={selectedMediaType === 'youtube'}
                                    onChange={(e) => setSelectedMediaType(e.target.value)}
                                    className="text-orange-500 focus:ring-orange-500"
                                />
                                <svg className="w-5 h-5 text-red-600" viewBox="0 0 576 512">
                                    <path
                                        fill="currentColor"
                                        d="M549.655 124.083c-6.281-23.65-24.862-42.231-48.511-48.511C458.281 64 288 64 288 64s-170.281 0-213.144 11.572c-23.65 6.281-42.231 24.862-48.511 48.511C16.001 166.946 16.001 256 16.001 256s0 89.054 10.344 131.917c6.281 23.65 24.862 42.231 48.511 48.511C117.719 448 288 448 288 448s170.281 0 213.144-11.572c23.65-6.281 42.231-24.862 48.511-48.511C559.999 345.054 559.999 256 559.999 256s0-89.054-10.344-131.917zM232 336V176l142.857 80L232 336z"
                                    />
                                </svg>
                                <span className="text-sm font-medium">YouTube</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="mediaType"
                                    value="instagram"
                                    checked={selectedMediaType === 'instagram'}
                                    onChange={(e) => setSelectedMediaType(e.target.value)}
                                    className="text-orange-500 focus:ring-orange-500"
                                />
                                <svg className="w-5 h-5" viewBox="0 0 448 512">
                                    <defs>
                                        <linearGradient id="ig-add-media-modal" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#f58529" />
                                            <stop offset="30%" stopColor="#dd2a7b" />
                                            <stop offset="60%" stopColor="#8134af" />
                                            <stop offset="100%" stopColor="#515bd4" />
                                        </linearGradient>
                                    </defs>
                                    <path
                                        fill="url(#ig-add-media-modal)"
                                        d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9 114.9-51.3 114.9-114.9S287.7 141 224.1 141zm0 186c-39.5 0-71.5-32-71.5-71.5s32-71.5 71.5-71.5 71.5 32 71.5 71.5-32 71.5-71.5 71.5zm146.4-194.3c0 14.9-12 26.9-26.9 26.9s-26.9-12-26.9-26.9 12-26.9 26.9-26.9 26.9 12 26.9 26.9zm76.1 27.2c-1.7-35.3-9.9-66.7-36.2-92.9C385.6 9.9 354.2 1.7 318.9 0 278.4-1.7 169.6-1.7 129.1 0 93.8 1.7 62.4 9.9 36.2 36.2 9.9 62.4 1.7 93.8 0 129.1c-1.7 40.5-1.7 149.3 0 189.8 1.7 35.3 9.9 66.7 36.2 92.9 26.2 26.2 57.6 34.4 92.9 36.2 40.5 1.7 149.3 1.7 189.8 0 35.3-1.7 66.7-9.9 92.9-36.2 26.2-26.2 34.4-57.6 36.2-92.9 1.7-40.5 1.7-149.3 0-189.8zM398.8 388c-7.8 19.6-22.9 34.7-42.5 42.5-29.4 11.7-99.2 9-132.3 9s-102.9 2.6-132.3-9c-19.6-7.8-34.7-22.9-42.5-42.5-11.7-29.4-9-99.2-9-132.3s-2.6-102.9 9-132.3c7.8-19.6 22.9-34.7 42.5-42.5C121.1 9 190.9 11.6 224 11.6s102.9-2.6 132.3 9c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 132.3s2.6 102.9-9 132.3z"
                                    />
                                </svg>
                                <span className="text-sm font-medium">Instagram</span>
                            </label>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Campaign</label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                            <div className="font-medium text-gray-900">{selectedCampaign?.name}</div>
                            <div className="text-gray-600">
                                {selectedCampaign?.commission?.model === 'fixed'
                                    ? `${selectedCampaign.commission.currency.symbol}${selectedCampaign.commission.amount}`
                                    : `${selectedCampaign.commission.percentage}%`}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedMediaType === 'youtube'
                            ? youtubeUrls
                                  .filter((link) => link.trim() !== '')
                                  .map((link, idx) => (
                                      <div
                                          key={idx}
                                          className="flex items-center justify-between text-sm bg-gray-100 px-3 py-1 rounded"
                                      >
                                          <div className="flex items-center space-x-2 min-w-0">
                                              <svg className="w-4 h-4 text-red-600 shrink-0" viewBox="0 0 576 512">
                                                  <path
                                                      fill="currentColor"
                                                      d="M549.655 124.083c-6.281-23.65-24.862-42.231-48.511-48.511C458.281 64 288 64 288 64s-170.281 0-213.144 11.572c-23.65 6.281-42.231 24.862-48.511 48.511C16.001 166.946 16.001 256 16.001 256s0 89.054 10.344 131.917c6.281 23.65 24.862 42.231 48.511 48.511C117.719 448 288 448 288 448s170.281 0 213.144-11.572c23.65-6.281 42.231-24.862 48.511-48.511C559.999 345.054 559.999 256 559.999 256s0-89.054-10.344-131.917zM232 336V176l142.857 80L232 336z"
                                                  />
                                              </svg>
                                              <a
                                                  href={link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:underline break-all"
                                              >
                                                  {link}
                                              </a>
                                          </div>
                                          <button
                                              type="button"
                                              onClick={() => setYoutubeUrls((prev) => prev.filter((_, i) => i !== idx))}
                                              className="text-red-500 hover:text-red-700 shrink-0"
                                              aria-label="Delete link"
                                          >
                                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                          </button>
                                      </div>
                                  ))
                            : instagramUrls
                                  .filter((link) => link.trim() !== '')
                                  .map((link, idx) => (
                                      <div
                                          key={idx}
                                          className="flex items-center justify-between text-sm bg-gray-100 px-3 py-1 rounded"
                                      >
                                          <div className="flex items-center space-x-2 min-w-0">
                                              <svg className="w-4 h-4 shrink-0" viewBox="0 0 448 512">
                                                  <defs>
                                                      <linearGradient
                                                          id="ig-add-media-list"
                                                          x1="0%"
                                                          y1="0%"
                                                          x2="100%"
                                                          y2="100%"
                                                      >
                                                          <stop offset="0%" stopColor="#f58529" />
                                                          <stop offset="30%" stopColor="#dd2a7b" />
                                                          <stop offset="60%" stopColor="#8134af" />
                                                          <stop offset="100%" stopColor="#515bd4" />
                                                      </linearGradient>
                                                  </defs>
                                                  <path
                                                      fill="url(#ig-add-media-list)"
                                                      d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9 114.9-51.3 114.9-114.9S287.7 141 224.1 141zm0 186c-39.5 0-71.5-32-71.5-71.5s32-71.5 71.5-71.5 71.5 32 71.5 71.5-32 71.5-71.5 71.5zm146.4-194.3c0 14.9-12 26.9-26.9 26.9s-26.9-12-26.9-26.9 12-26.9 26.9-26.9 26.9 12 26.9 26.9zm76.1 27.2c-1.7-35.3-9.9-66.7-36.2-92.9C385.6 9.9 354.2 1.7 318.9 0 278.4-1.7 169.6-1.7 129.1 0 93.8 1.7 62.4 9.9 36.2 36.2 9.9 62.4 1.7 93.8 0 129.1c-1.7 40.5-1.7 149.3 0 189.8 1.7 35.3 9.9 66.7 36.2 92.9 26.2 26.2 57.6 34.4 92.9 36.2 40.5 1.7 149.3 1.7 189.8 0 35.3-1.7 66.7-9.9 92.9-36.2 26.2-26.2 34.4-57.6 36.2-92.9 1.7-40.5 1.7-149.3 0-189.8zM398.8 388c-7.8 19.6-22.9 34.7-42.5 42.5-29.4 11.7-99.2 9-132.3 9s-102.9 2.6-132.3-9c-19.6-7.8-34.7-22.9-42.5-42.5-11.7-29.4-9-99.2-9-132.3s-2.6-102.9 9-132.3c7.8-19.6 22.9-34.7 42.5-42.5C121.1 9 190.9 11.6 224 11.6s102.9-2.6 132.3 9c19.6 7.8 34.7 22.9 42.5 42.5 11.7 29.4 9 99.2 9 132.3s2.6 102.9-9 132.3z"
                                                  />
                                              </svg>
                                              <a
                                                  href={link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:underline break-all"
                                              >
                                                  {link}
                                              </a>
                                          </div>
                                          <button
                                              type="button"
                                              onClick={() =>
                                                  setInstagramUrls((prev) => prev.filter((_, i) => i !== idx))
                                              }
                                              className="text-red-500 hover:text-red-700 shrink-0"
                                              aria-label="Delete link"
                                          >
                                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                          </button>
                                      </div>
                                  ))}
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                            {selectedMediaType === 'youtube' ? 'YouTube video links' : 'Instagram post links'}
                        </label>
                        {selectedMediaType === 'youtube' ? (
                            <input
                                type="text"
                                value={newYoutubeLink}
                                onChange={(e) => setNewYoutubeLink(e.target.value)}
                                placeholder="Paste your YouTube video link here..."
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        ) : (
                            <input
                                type="text"
                                value={newInstagramLink}
                                onChange={(e) => setNewInstagramLink(e.target.value)}
                                placeholder="Paste your Instagram post link here..."
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        )}
                    </div>

                    {mediaError && <p className="text-red-600 text-sm">{mediaError}</p>}
                    {mediaSuccess && <p className="text-green-600 text-sm">{mediaSuccess}</p>}

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedMediaType === 'youtube' && newYoutubeLink.trim() === '') return;
                                if (selectedMediaType === 'instagram' && newInstagramLink.trim() === '') return;
                                if (selectedMediaType === 'youtube') {
                                    setYoutubeUrls((prev) => [...prev, newYoutubeLink.trim()]);
                                } else {
                                    setInstagramUrls((prev) => [...prev, newInstagramLink.trim()]);
                                }
                                setNewYoutubeLink('');
                                setNewInstagramLink('');
                            }}
                            className="border border-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100"
                        >
                            + Add more
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedMediaType === 'youtube' && newYoutubeLink.trim()) {
                                    setYoutubeUrls((prev) => [...prev, newYoutubeLink.trim()]);
                                } else if (selectedMediaType === 'instagram' && newInstagramLink.trim()) {
                                    setInstagramUrls((prev) => [...prev, newInstagramLink.trim()]);
                                }
                                handleAddMedia();
                            }}
                            disabled={
                                selectedMediaType === 'youtube'
                                    ? youtubeUrls.filter((link) => link.trim() !== '').length === 0 &&
                                      newYoutubeLink.trim() === ''
                                    : instagramUrls.filter((link) => link.trim() !== '').length === 0 &&
                                      newInstagramLink.trim() === ''
                            }
                            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${
                                selectedMediaType === 'youtube'
                                    ? youtubeUrls.filter((link) => link.trim() !== '').length === 0 &&
                                      newYoutubeLink.trim() === ''
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : mediaLoading
                                          ? 'bg-orange-400'
                                          : 'bg-orange-500 hover:bg-orange-600'
                                    : instagramUrls.filter((link) => link.trim() !== '').length === 0 &&
                                        newInstagramLink.trim() === ''
                                      ? 'bg-gray-300 cursor-not-allowed'
                                      : mediaLoading
                                        ? 'bg-orange-400'
                                        : 'bg-orange-500 hover:bg-orange-600'
                            }`}
                        >
                            {mediaLoading ? 'Adding...' : 'Continue'}
                        </button>
                    </div>
                </div>
            </div>
        );

        const fullContractModal =
            fullContractOpen && contract ? (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4">
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="bg-white text-gray-900 rounded-2xl w-full max-w-3xl border border-gray-200 shadow-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-xs text-gray-500 truncate">
                                    {companyName} / {brandName || companyName}
                                </p>
                                <h2 className="text-lg font-bold text-gray-900 truncate pr-2">Full contract</h2>
                                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{campaign.name}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFullContractOpen(false)}
                                className="p-2 rounded-lg text-gray-400 hover:text-gray-800 hover:bg-gray-100 shrink-0"
                                aria-label="Close"
                            >
                                <FaTimes className="text-lg" />
                            </button>
                        </div>

                        <div className="px-4 sm:px-5 py-4">
                            <div className="rounded-xl border border-gray-200 bg-[#F3F1EA] px-4 py-4 sm:px-5 sm:py-6">
                                <div className="text-center text-sm font-extrabold text-gray-900 tracking-wide">
                                    INFLUENCER COLLABORATION AGREEMENT
                                </div>

                                <div className="mt-4 text-xs sm:text-sm text-gray-700 leading-relaxed">
                                    This agreement is between{' '}
                                    <span className="font-extrabold text-gray-900">{brandName || companyName}</span>{' '}
                                    ("Brand") and{' '}
                                    <span className="font-extrabold text-gray-900">
                                        {String(campaign?.influencerName || 'Influencer')}
                                    </span>{' '}
                                    ("Influencer") for the{' '}
                                    <span className="font-extrabold text-gray-900">{campaign.name}</span> campaign.
                                </div>

                                <div className="mt-5 space-y-3 text-gray-800">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-extrabold">Deliverables:</span>
                                            {contract?.deliverablesText ? (
                                                <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm">
                                                    {contract.deliverablesText}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm">
                                                    —
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-extrabold">Compensation:</span>
                                            <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm tabular-nums">
                                                ₹{Number(contract?.agreedRateInr || 0).toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                    </div>

                                    {hasPaymentTermDays ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="font-extrabold">Payment term:</span>
                                            <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-sm">
                                                {Number.isFinite(paymentTermDaysNum) ? paymentTermDaysNum : paymentTermDaysRaw} days
                                            </span>
                                        </div>
                                    ) : null}
                                </div>

                                <div className="mt-6 italic text-gray-600 font-semibold">
                                    Both parties agree to the terms above.
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                    <div className="text-sm font-semibold text-emerald-800">
                                        Brand signature ({brandName || companyName})
                                    </div>
                                    <div className="mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold bg-emerald-100 border-emerald-200 text-emerald-800">
                                        {String(contract?.brandSignedBy || brandName || companyName || 'Brand')} signed
                                    </div>
                                </div>
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                    <div className="text-sm font-semibold text-emerald-800">Your signature</div>
                                    {influencerHasSignedContract ? (
                                        <div className="mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold bg-emerald-100 border-emerald-200 text-emerald-800">
                                            {`${String(contract?.influencerSignedBy || 'You')} signed`}
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={influencerSignLoading || !token || !campaignId}
                                            onClick={handleInfluencerContractSign}
                                            className={[
                                                'mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold text-left transition-colors',
                                                influencerSignLoading
                                                    ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-wait'
                                                    : 'bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50 cursor-pointer',
                                            ].join(' ')}
                                        >
                                            {influencerSignLoading ? 'Saving signature…' : 'Click to sign'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-4 sm:px-5 py-4 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => window.print()}
                                className="px-4 py-2 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 text-sm font-medium hover:bg-gray-100"
                            >
                                Download PDF
                            </button>
                            <button
                                type="button"
                                disabled={
                                    !influencerHasSignedContract ||
                                    confirmCampaignLoading ||
                                    !token ||
                                    !campaignId
                                }
                                onClick={handleConfirmCampaign}
                                className={[
                                    'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center gap-2',
                                    !influencerHasSignedContract ||
                                    confirmCampaignLoading ||
                                    !token ||
                                    !campaignId
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#16a34a] hover:bg-[#15803d] text-white',
                                ].join(' ')}
                            >
                                <FaCheck className="text-xs shrink-0" aria-hidden />
                                {confirmCampaignLoading ? 'Confirming…' : 'Confirm campaign'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setFullContractOpen(false)}
                                className="px-4 py-2 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            ) : null;

        return (
            <>
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    className="bg-white rounded-[10px] border border-gray-200/90 shadow-sm overflow-hidden mb-4"
                >
                    <div className="p-4 sm:p-5 flex gap-4 items-start">
                        <div
                            className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-pink-100 to-rose-50 border border-pink-200 flex items-center justify-center shrink-0 text-pink-800 font-bold text-sm sm:text-base"
                            aria-hidden
                        >
                            {brandInitials(brandName || companyName)}
                        </div>
                        <div className="flex-1 min-w-0 pr-2">
                            <p className="text-xs sm:text-sm text-gray-500 truncate">
                                {companyName} / {brandName}
                            </p>
                            <h3 className="text-sm sm:text-lg font-bold text-gray-900 mt-0.5 leading-snug truncate">
                                {campaign.name}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                                {campaign.description || 'No description provided'}
                            </p>
                        </div>
                        <div className="shrink-0 flex items-center gap-1 sm:gap-2 pt-0.5">
                            <span
                                className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs sm:text-sm font-semibold whitespace-nowrap ${
                                    agreed ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-700'
                                }`}
                            >
                                <span className="mr-1">•</span>
                                {agreed ? 'Accepted' : 'Negotiating'}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowAnalytics(true)}
                                className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-gray-50 transition-colors"
                                title="View analytics"
                            >
                                <FaChartLine className="text-sm sm:text-base" />
                            </button>
                        </div>
                    </div>

                    <div className="bg-gray-100/90 border-t border-gray-200/80 px-3 sm:px-5 py-4">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-3 gap-y-4 text-center sm:text-left">
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Type
                                </p>
                                <p className={`text-sm font-bold ${campaignTypeClass(campaign.campaignType)}`}>
                                    {campaign.campaignType || '—'}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Commission
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const commissionForDisplay =
                                            campaign.agreement?.commission || campaign.commission;
                                        setCommissionModal({
                                            isOpen: true,
                                            commission: commissionForDisplay,
                                            campaignName: campaign.name,
                                            campaignType: campaign.campaignType,
                                            agreementStatus: campaign.agreement?.status || '',
                                        });
                                    }}
                                    className="inline-flex items-center justify-center sm:justify-start gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm w-full sm:w-auto"
                                >
                                    <FaEye className="text-xs shrink-0" />
                                    <span>View details</span>
                                </button>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Brand
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setBrandDetailsModal({
                                            isOpen: true,
                                            companyName: companyName || '',
                                            brandName: brandName || '',
                                            companyEmail: companyEmail || null,
                                            companyPhone: companyPhone || null,
                                        });
                                    }}
                                    className="inline-flex items-center justify-center sm:justify-start gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm w-full sm:w-auto"
                                >
                                    <FaEye className="text-xs shrink-0" />
                                    <span>View details</span>
                                </button>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Starts
                                </p>
                                <p className="text-sm font-bold text-gray-900">{startStr}</p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Expires
                                </p>
                                <p className="text-sm font-bold text-gray-900">{expiresStr}</p>
                            </div>
                            <div>
                                <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                                    Rate
                                </p>
                                <p className="text-sm font-bold text-gray-900">
                                    {rateParts.main}
                                    {rateParts.showIndicative && (
                                        <span className="font-normal text-gray-500 text-xs ml-1">Indicative</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    <AcceptedCampaignProgressStepper
                        currentStepIndex={getAcceptedCampaignCurrentStepIndex(campaign, optimisticPricingAgreed)}
                    />

                    <div className="px-3 sm:px-4 pb-4 space-y-3 border-t border-[1px solid #9DE5C0] bg-[#E8F8EF] pt-3">
                        {showContractPanel ? (
                            <div className="rounded-2xl border border-gray-200 bg-[#F3F1EA] overflow-hidden">
                                <div className="px-4 py-3 bg-white border-b border-gray-200">
                                    <p className="text-sm font-bold text-gray-900">
                                        Contract ready — your signature needed
                                    </p>
                                    <p className="text-xs text-gray-600 mt-0.5">
                                        The brand has signed. Tap <span className="font-semibold">Click to sign</span> in
                                        Your signature, then tap <span className="font-semibold">Confirm campaign</span>.
                                    </p>
                                </div>

                                <div className="px-4 py-4">
                                    <div className="text-center text-xs sm:text-sm font-extrabold text-gray-900 tracking-wide">
                                        INFLUENCER COLLABORATION AGREEMENT
                                    </div>
                                    <div className="mt-3 text-xs sm:text-sm text-gray-700 leading-relaxed">
                                        This agreement is between{' '}
                                        <span className="font-extrabold text-gray-900">{brandName || companyName}</span>{' '}
                                        ("Brand") and{' '}
                                        <span className="font-extrabold text-gray-900">you</span> ("Influencer") for the{' '}
                                        <span className="font-extrabold text-gray-900">{campaign.name}</span> campaign.
                                    </div>

                                    <div className="mt-4 space-y-2 text-gray-800">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {contract?.deliverablesText ? (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="font-extrabold">Deliverables:</span>
                                                    <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 shadow-sm">
                                                        {contract.deliverablesText}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div />
                                            )}
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-extrabold">Compensation:</span>
                                                <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 shadow-sm tabular-nums">
                                                    ₹{Number(contract?.agreedRateInr || campaign?.pipelineNegotiation?.agreedRateInr || 0).toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                        </div>
                                        {hasPaymentTermDays ? (
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-extrabold">Payment term:</span>
                                                <span className="inline-flex items-center rounded-lg bg-white border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-800 shadow-sm">
                                                    {Number.isFinite(paymentTermDaysNum) ? paymentTermDaysNum : paymentTermDaysRaw} days
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                            <div className="text-sm font-semibold text-emerald-800">
                                                Brand signature ({brandName || companyName})
                                            </div>
                                            <div className="mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold bg-emerald-100 border-emerald-200 text-emerald-800">
                                                {String(contract?.brandSignedBy || brandName || companyName || 'Brand')} signed
                                            </div>
                                        </div>
                                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                                            <div className="text-sm font-semibold text-emerald-800">Your signature</div>
                                            {influencerHasSignedContract ? (
                                                <div className="mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold bg-emerald-100 border-emerald-200 text-emerald-800">
                                                    {`${String(contract?.influencerSignedBy || 'You')} signed`}
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled={influencerSignLoading || !token || !campaignId}
                                                    onClick={handleInfluencerContractSign}
                                                    className={[
                                                        'mt-2 w-full rounded-lg border border-dashed px-4 py-3 text-sm font-semibold text-left transition-colors',
                                                        influencerSignLoading
                                                            ? 'bg-gray-100 border-gray-200 text-gray-500 cursor-wait'
                                                            : 'bg-white border-emerald-200 text-emerald-800 hover:bg-emerald-50 cursor-pointer',
                                                    ].join(' ')}
                                                >
                                                    {influencerSignLoading ? 'Saving signature…' : 'Click to sign'}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="mt-4 flex items-center gap-2">
                                        <button
                                            type="button"
                                            disabled={
                                                !influencerHasSignedContract ||
                                                confirmCampaignLoading ||
                                                !token ||
                                                !campaignId
                                            }
                                            onClick={handleConfirmCampaign}
                                            className={[
                                                'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold shadow-sm transition-colors',
                                                !influencerHasSignedContract ||
                                                confirmCampaignLoading ||
                                                !token ||
                                                !campaignId
                                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                    : 'bg-[#16a34a] hover:bg-[#15803d] text-white',
                                            ].join(' ')}
                                        >
                                            <FaCheck className="text-xs shrink-0" />
                                            {confirmCampaignLoading ? 'Confirming…' : 'Confirm campaign'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFullContractOpen(true)}
                                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 text-xs sm:text-sm font-semibold hover:bg-gray-50 transition-colors"
                                        >
                                            <FaEye className="text-xs shrink-0" aria-hidden />
                                            View full contract
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <div>
                            <h4 className="text-[10px] md:text-sm font-semibold text-[#0E7A40]">
                                {pipelineStageId === 'confirmed'
                                    ? '✓ Campaign confirmed — you are live!'
                                    : pipelineStageId === 'contracted'
                                      ? influencerHasSignedContract
                                          ? 'Contract signed — confirm campaign to go live'
                                          : showContractPanel
                                            ? 'Contracting — sign to proceed'
                                            : 'Contracting — in progress'
                                      : agreed
                                        ? 'Pricing accepted'
                                        : 'Negotiating — pricing proposal from brand'}
                            </h4>
                            <p className="text-[10px] md:text-sm text-[#18A558] mt-0.5">
                                {pipelineStageId === 'confirmed'
                                    ? 'You can now add your media details and get your affiliate link.'
                                    : pipelineStageId === 'contracted'
                                      ? influencerHasSignedContract
                                          ? 'Tap Confirm campaign when you are ready to finalize with the brand.'
                                          : showContractPanel
                                            ? 'Tap Click to sign in Your signature, then Confirm campaign.'
                                            : 'The brand is preparing your contract. You will be able to sign here when it is ready.'
                                      : agreed
                                        ? 'You accepted this offer. The brand may generate your contract next.'
                                        : 'Review the brand offer in commission details, then proceed with the campaign brief.'}
                            </p>
                        </div>

                        {pipelineStageId !== 'contracted' && pipelineStageId !== 'confirmed' ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 text-center sm:text-left">
                                <p className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 tracking-tight">
                                    {rateParts.main}
                                </p>
                                <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500 mt-0.5">Indicative rate</p>
                            </div>
                            <div
                                className={`rounded-xl border px-3 py-3 text-center sm:text-left ${
                                    brandOffer.hasOffer || latestBrandOfferInr != null
                                        ? 'border-amber-200 bg-[#fff9e6]'
                                        : 'border-gray-200 bg-amber-50/40'
                                }`}
                            >
                                <p className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 tracking-tight">
                                    {brandOfferDisplay}
                                </p>
                                <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500 mt-0.5">Brand&apos;s latest offer</p>
                            </div>
                        </div>
                        ) : null}

                        {pipelineStageId !== 'contracted' && pipelineStageId !== 'confirmed' ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-3 min-h-[110px]">
                            <p className="text-[10px] sm:text-xs lg:text-sm font-medium uppercase tracking-wide text-gray-500 mb-2">
                                Message history
                            </p>
                            {threadLoading ? (
                                <p className="text-[10px] md:text-sm text-gray-500 text-center px-2">Loading messages…</p>
                            ) : thread.length === 0 ? (
                                <p className="text-[10px] md:text-sm text-gray-500 text-center px-2">
                                    No messages yet — send a counter-offer to begin.
                                </p>
                            ) : (
                                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                                    {thread.map((m, idx) => {
                                        const isBrand = m.actor === 'company';
                                        return (
                                            <div key={`${m.at || idx}`} className={isBrand ? 'text-left' : 'text-right'}>
                                                <div className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-400 mb-1">
                                                    {isBrand ? (brandName || companyName || 'Brand') : 'You'}
                                                </div>
                                                <div
                                                    className={[
                                                        'inline-block max-w-[92%] rounded-xl px-3 py-2 text-[10px] md:text-sm leading-relaxed border',
                                                        isBrand
                                                            ? 'bg-gray-50 text-gray-800 border-gray-200'
                                                            : 'bg-[#FFE9DB] text-[#7A3B1C] border-orange-200',
                                                    ].join(' ')}
                                                >
                                                    {m?.amountInr != null ? (
                                                        <div className="font-semibold text-gray-900 mb-0.5">
                                                            ₹{Number(m.amountInr).toLocaleString('en-IN')}
                                                        </div>
                                                    ) : null}
                                                    {m.text}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        ) : null}

                        {pipelineStageId !== 'confirmed' &&
                            (!agreed ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label
                                            htmlFor={`counter-offer-${campaign._id}`}
                                            className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600"
                                        >
                                            Your counter-offer (₹)
                                        </label>
                                        <input
                                            id={`counter-offer-${campaign._id}`}
                                            type="text"
                                            value={counterAmount}
                                            onChange={(e) => setCounterAmount(e.target.value)}
                                            placeholder="e.g. 95000"
                                            className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[10px] md:text-sm text-gray-900"
                                        />
                                    </div>
                                    <div>
                                        <label
                                            htmlFor={`negotiation-msg-${campaign._id}`}
                                            className="text-[10px] sm:text-xs lg:text-sm font-medium text-gray-600"
                                        >
                                            Message
                                        </label>
                                        <input
                                            id={`negotiation-msg-${campaign._id}`}
                                            type="text"
                                            value={counterMessage}
                                            onChange={(e) => setCounterMessage(e.target.value)}
                                            placeholder="Add a message…"
                                            className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[10px] md:text-sm text-gray-900"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-xl border border-emerald-200 bg-[#E6F4EA] px-3 py-3 sm:px-4">
                                    <p className="text-xs sm:text-sm font-semibold text-emerald-800">Accepted</p>
                                    <p className="text-[10px] sm:text-xs text-emerald-700 mt-1">
                                        You accepted the brand&apos;s pricing. Counter-offers are closed for this deal.
                                    </p>
                                </div>
                            ))}

                        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-2.5 items-stretch sm:items-center">
                            {!agreed ? (
                            <>
                            <button
                                type="button"
                                disabled={accepting || !canAcceptOffer}
                                onClick={async () => {
                                    const offerToAccept = brandOfferAmountInr ?? latestBrandOfferInr;
                                    if (!token || !campaignId || offerToAccept == null) return;
                                    setAccepting(true);
                                    try {
                                        const res = await acceptCampaignOffer(token, campaignId, { agreedRateInr: offerToAccept });
                                        const list = Array.isArray(res?.data?.thread) ? res.data.thread : [];
                                        setThread(list);
                                        setOptimisticPricingAgreed(true);
                                        toast.success('Offer accepted');
                                        onCampaignsRefresh?.();
                                    } catch (e) {
                                        toast.error(e?.message || 'Failed to accept offer');
                                    } finally {
                                        setAccepting(false);
                                    }
                                }}
                                className={[
                                    'inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-semibold shadow-sm transition-colors',
                                    accepting || !canAcceptOffer
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-[#16a34a] hover:bg-[#15803d] text-white',
                                ].join(' ')}
                                title={!canAcceptOffer ? 'No brand offer available to accept yet' : 'Accept brand offer'}
                            >
                                <FaCheck className="text-xs shrink-0" />
                                {accepting ? 'Accepting…' : `Accept ${offerForAcceptLabel}`}
                            </button>
                            <button
                                type="button"
                                disabled={sending || (!counterAmount.trim() && !counterMessage.trim())}
                                onClick={async () => {
                                    if (!token || !campaignId) return;
                                    setSending(true);
                                    try {
                                        const amt = counterAmount.trim() ? Number(counterAmount.trim().replace(/,/g, '')) : null;
                                        const res = await sendCampaignCounterOffer(token, campaignId, {
                                            amountInr: amt,
                                            text: counterMessage,
                                        });
                                        const list = Array.isArray(res?.data?.thread) ? res.data.thread : [];
                                        setThread(list);
                                        setCounterAmount('');
                                        setCounterMessage('');
                                        toast.success('Counter-offer sent');
                                    } catch (e) {
                                        toast.error(e?.message || 'Failed to send counter-offer');
                                    } finally {
                                        setSending(false);
                                    }
                                }}
                                className="inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-orange-500 bg-white text-orange-600 hover:bg-orange-50 text-xs sm:text-sm font-semibold transition-colors"
                                title="Send counter-offer to the brand"
                            >
                                <FaComment className="text-xs shrink-0" aria-hidden />
                                {sending ? 'Sending…' : 'Send counter-offer'}
                            </button>
                            <button
                                type="button"
                                disabled
                                className="inline-flex flex-1 sm:flex-initial items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#dc3545] text-white text-xs sm:text-sm font-semibold opacity-60 cursor-not-allowed"
                                title="Contact support to change campaign status"
                            >
                                <FaTimes className="text-xs shrink-0" />
                                Walk away
                            </button>
                            </>
                            ) : null}
                            {canAddMedia && (campaign.campaignType === 'Advertising' || campaign.campaignType === 'Hybrid') && (
                                <div className="pt-1 flex flex-row gap-2">
                                    <button
                                        type="button"
                                        className="inline-flex w-full sm:w-auto items-center justify-center bg-[#18A558] hover:bg-[#0F8745] text-white gap-2 px-2 py-1 rounded-lg text-[10px] md:text-sm font-semibold shadow-sm transition-all"
                                        onClick={() => {
                                            if (campaign && campaign._id) {
                                                setSelectedCampaign(campaign);
                                                setIsDialogOpen(true);
                                            } else {
                                                toast.error('Invalid campaign data. Please try again.');
                                            }
                                        }}
                                    >
                                        <span >+ Add media details</span>
                                        {/* <span className="sm:hidden">+ Add media</span> */}
                                    </button>

                                    {!!campaign?.landingPage && (
                                        <button
                                            type="button"
                                            className="inline-flex w-full sm:w-auto items-center justify-center bg-[#F0F1F3] text-[#6b6f79] border border-[1px solidrgb(245, 250, 249)] gap-2 px-2 py-1 rounded-lg text-[10px] md:text-sm font-semibold shadow-sm transition-all"
                                            onClick={() => {
                                                try {
                                                    window.open(campaign.landingPage, '_blank', 'noopener,noreferrer');
                                                } catch (e) {
                                                    toast.error('Unable to open link. Please try again.');
                                                }
                                            }}
                                            title="Open landing page in a new tab"
                                        >
                                            <span>🔗 Get affiliate link</span>
                                            {/* <span className="sm:hidden">🔗 Affiliate link</span> */}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        
                    </div>
                </motion.div>
                {addMediaModal}
                {fullContractModal}
            </>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 hover:shadow-lg transition-all duration-200"
        >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-500">{companyName}</span>
                        <span className="hidden sm:inline text-gray-400">/</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-500">{brandName}</span>
                    </div>
                    <h3 className="text-sm md:text-lg lg:text-base sm:text-lg font-semibold text-gray-800 truncate">
                        {campaign.name}
                    </h3>
                    <p className="text-sm md:text-lg lg:text-gray-600 mt-1 text-sm line-clamp-2">
                        {campaign.description || 'No description provided'}
                    </p>
                </div>
                <div className="flex items-center space-x-2">
                    <span
                        className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                            campaign.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : campaign.status === 'expired'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-yellow-100 text-yellow-800'
                        }`}
                    >
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">Campaign Type</p>
                    <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            campaign.campaignType === 'Advertising'
                                ? 'bg-blue-100 text-blue-800'
                                : campaign.campaignType === 'Affiliate'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-orange-100 text-orange-800'
                        }`}
                    >
                        {campaign.campaignType}
                    </span>
                </div>
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">Commission</p>
                    <button
                        type="button"
                        onClick={() => {
                            const commissionForDisplay = campaign.agreement?.commission || campaign.commission;
                            setCommissionModal({
                                isOpen: true,
                                commission: commissionForDisplay,
                                campaignName: campaign.name,
                                campaignType: campaign.campaignType,
                                agreementStatus: campaign.agreement?.status || '',
                            });
                        }}
                        className="flex items-center gap-1 text-orange-600 hover:text-orange-700 transition-colors duration-200"
                        title="View Commission Details"
                    >
                        <FaEye className="text-xs" />
                        <span className="text-xs font-medium">View Details</span>
                    </button>
                </div>
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">Brand Details</p>
                    <button
                        type="button"
                        onClick={() => {
                            setBrandDetailsModal({
                                isOpen: true,
                                companyName: companyName || '',
                                brandName: brandName || '',
                                companyEmail: companyEmail || null,
                                companyPhone: companyPhone || null,
                            });
                        }}
                        className="flex items-center gap-1 text-orange-600 hover:text-orange-700 transition-colors duration-200"
                        title="View Brand Details"
                    >
                        <FaEye className="text-xs" />
                        <span className="text-xs font-medium">View Details</span>
                    </button>
                </div>
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">Starts</p>
                    <p className="font-medium text-sm sm:text-base">
                        {campaign.startDate && !isNaN(new Date(campaign.startDate).getTime())
                            ? new Date(campaign.startDate).toLocaleDateString()
                            : 'Not set'}
                    </p>
                </div>
                <div>
                    <p className="text-xs sm:text-sm text-gray-500">Expires</p>
                    <p className="font-medium text-sm sm:text-base">
                        {campaign.expiresAt && !isNaN(new Date(campaign.expiresAt).getTime())
                            ? new Date(campaign.expiresAt).toLocaleDateString()
                            : 'Not set'}
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
